import React from 'react';
import { Document, Page, Text, Image, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment';
import Logo from '../../../assets/images/RangeBadge1.png';

// Helper to chunk an array into groups of 'size'
const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const parseFullName = (fullName) => {
  const match = fullName.match(/^([^,]+),\s+([\w\s]+?)(?:\s+([\w]+))?$/);

  if (!match) {
    return { lastname: '', firstname: '', middlename: '' };
  }

  return {
    lastname: match[1].trim(),
    firstname: match[2].trim(),
    middlename: match[3] ? match[3].trim() : '',
  };
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10,
  },
  // Header row: logo (left), Title (center), Date/time (right)
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  headerLogo: {
    width: 50,
    height: 50,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  headerDate: {
    fontSize: 9,
    textAlign: 'right',
  },

  // Each "row" of images (3 across)
  imagesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  imageContainer: {
    width: '30%', // 3 images per row
    alignItems: 'center',
  },
  // Number circle above the image
  numberCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#0153a3',
    color: '#fff',
    fontSize: 10,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 5,
  },
  photo: {
    height: '150px',
    objectFit: 'contain',
  },

  // "Lineup Data" page
  dataTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 10,
    fontSize: 16,
  },
  personDataBlock: {
    marginBottom: 10,
    padding: 5,
    // backgroundColor: '#eee',
  },
  // If you want multi-column rows, but we’ll just do single lines here
  personDataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
});

const TularePdfView = ({ imageData = [] }) => {
  const currentDateTime = moment().format('MMMM DD, YYYY - h:mm A');

  // 1) Break the images into chunks of 6 (each chunk = 1 page).
  const chunkedBySix = chunkArray(imageData, 6);

  return (
    <Document>
      {/* ================== IMAGE PAGES ================== */}
      {chunkedBySix.map((sixGroup, pageIndex) => {
        // Now each chunk of 6 can be split into two rows of 3
        const rowsOfThree = chunkArray(sixGroup, 3);

        return (
          <Page key={pageIndex} style={styles.page} size="A4">
            {/* Header */}
            <View style={styles.headerRow}>
              <Image style={styles.headerLogo} source={Logo} />
              <Text style={styles.headerTitle}>Tulare County Probation Department</Text>
              <Text style={styles.headerDate}>
                Date Printed:
                {'\n'}
                {moment().format('MM/DD/YYYY')}
              </Text>
            </View>

            {/* Up to 2 rows (3 images each) per page */}
            {rowsOfThree.map((rowGroup, rowIndex) => (
              <View key={rowIndex} style={styles.imagesRow}>
                {rowGroup.map((person, colIndex) => {
                  // overall image index
                  const overallIndex = pageIndex * 6 + rowIndex * 3 + colIndex;
                  return (
                    <View style={styles.imageContainer} key={overallIndex} wrap={false}>
                      {/* Number circle on top (no ID) */}
                      <View>
                        <Text>{overallIndex + 1}</Text>
                      </View>
                      <Image
                        style={styles.photo}
                        src={
                          person.uploadedImage
                            ? person.uploadedImage // remote URL
                            : `data:image/jpeg;base64,${person.FileObject}`
                        }
                      />
                    </View>
                  );
                })}
              </View>
            ))}
          </Page>
        );
      })}

      {/* ================== DATA PAGE ================== */}
      {/* 
        If the list of people is large and spills over multiple pages,
        the "fixed" prop ensures the heading is repeated at the top of each new page
      */}
      <Page size="A4" style={styles.page} wrap>
        <View fixed>
          <Text style={styles.dataTitle}>Lineup Data - {currentDateTime}</Text>
        </View>

        {imageData.map((person, idx) => (
          <View key={idx} style={styles.personDataBlock}>
            {/* Index + ID */}
            <Text>
              [{idx + 1}. {person.ptsImageID}]
            </Text>
            <Text>Last Name: {parseFullName(person.FullName).lastname}</Text>
            <Text>First Name: {parseFullName(person.FullName).firstname}</Text>
            <Text>Middle Name: {parseFullName(person.FullName).middlename}</Text>
            <Text>Photo ID: {person.ptsImageID}</Text>
            <Text>Race: {person.Race}</Text>
            <Text>Sex: {person.Sex}</Text>
            <Text>DOB: {moment(person.Born).format('MM/DD/YYYY')}</Text>
            <Text>Eye Color: {person.EyeColor}</Text>
            <Text>Hair Color: {person.HairColor}</Text>
            <Text>Height: {person.Height}</Text>
            {/* <Text>Height - e.g.: {person.heightRaw}</Text> */}
            <Text>Weight: {person.Weight}</Text>
          </View>
        ))}
      </Page>
    </Document>
  );
};

export default TularePdfView;
